/* PURE v2 media slot (snippets/pv-media.liquid).
   - The still <img>/<picture> is always in the DOM and visible; a <video> fades in only once it is PLAYING
     (class is-playing on the video itself).
   - A slot may hold two videos (desktop + mobile, one hidden by CSS); only the displayed one plays.
   - Plays muted/inline when >= 25% in view, pauses offscreen. Never autoplays under
     prefers-reduced-motion (the play button still lets the visitor opt in).
   - A load error on an MP4 hides that video: the still stays. */
if (!window.customElements.get('pv-media')) {
  window.customElements.define(
    'pv-media',
    class PvMedia extends HTMLElement {
      connectedCallback() {
        this.videos = Array.from(this.querySelectorAll('video'));
        if (this.videos.length === 0 || this.dataset.pvReady) return;
        this.dataset.pvReady = '1';
        this.button = this.querySelector('.pv-media__toggle');
        this.userPaused = false;
        this.inView = false;
        this.reduce = window.matchMedia('(prefers-reduced-motion: reduce)');

        this.videos.forEach((video) => {
          video.muted = true;
          video.addEventListener('playing', () => {
            video.classList.add('is-playing');
            this.sync();
          });
          video.addEventListener('pause', () => this.sync());
          video.addEventListener('error', () => this.fail(video));
        });

        if (this.button) {
          this.button.addEventListener('click', () => {
            const video = this.current();
            if (!video) return;
            if (video.paused) {
              this.userPaused = false;
              this.play(true);
            } else {
              this.userPaused = true;
              video.pause();
            }
          });
        }

        if ('IntersectionObserver' in window) {
          this.observer = new IntersectionObserver(
            (entries) => {
              entries.forEach((entry) => {
                this.inView = entry.isIntersecting;
                if (entry.isIntersecting) {
                  if (!this.reduce.matches && !this.userPaused) this.play(false);
                } else {
                  this.pauseAll();
                }
              });
            },
            { threshold: 0.25 }
          );
          this.observer.observe(this);
        } else if (!this.reduce.matches) {
          this.play(false);
        }

        if (this.videos.length > 1) {
          this.onResize = () => {
            if (!this.inView || this.reduce.matches || this.userPaused) return;
            this.play(false);
          };
          window.addEventListener('resize', this.onResize, { passive: true });
        }
      }

      disconnectedCallback() {
        if (this.observer) this.observer.disconnect();
        if (this.onResize) window.removeEventListener('resize', this.onResize);
      }

      current() {
        return this.videos.find((v) => !v.classList.contains('is-failed') && v.offsetWidth > 0) || null;
      }

      pauseAll() {
        this.videos.forEach((v) => {
          if (!v.paused) v.pause();
        });
      }

      play(fromUser) {
        const video = this.current();
        this.videos.forEach((v) => {
          if (v !== video && !v.paused) v.pause();
        });
        if (!video) {
          this.classList.add('is-failed');
          return;
        }
        if (video.preload === 'none') video.preload = 'auto';
        const attempt = video.play();
        if (attempt && attempt.catch) {
          attempt.catch(() => {
            // Autoplay blocked (e.g. low-power mode): the still stays, the play button shows.
            this.sync();
            if (fromUser) this.fail(video);
          });
        }
      }

      fail(video) {
        video.classList.add('is-failed');
        video.classList.remove('is-playing');
        if (!this.current()) this.classList.add('is-failed');
      }

      sync() {
        const video = this.current();
        const paused = !video || video.paused;
        this.classList.toggle('is-paused', paused);
        if (this.button) {
          this.button.setAttribute(
            'aria-label',
            paused ? this.button.dataset.labelPlay : this.button.dataset.labelPause
          );
        }
      }
    }
  );
}
